import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Eye, EyeOff, EyeOffIcon, EyeIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  loginUser,
  resendOtp,
  signUpUser,
  verifyOtp,
} from "../../Redux/ReduxSlices/authSlice";
import Notification from "../../context/Notification";
import ForgotPasswordModal from "../../forgetPassword/ForgotPasswordModal";
import { toast } from "sonner";
import { set } from "date-fns";
import SuccessPopup from "../customeLoader/SuccessPopup";

export default function AuthModal({ isOpen, onClose, defaultTab = "signup" }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [otpTimer, setOtpTimer] = useState(120); // 2 minutes in seconds
  const [canResendOtp, setCanResendOtp] = useState(false);
  const timerRef = useRef(null);
  const dispatch = useDispatch();
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Add these new states
  const [loginOtpVerification, setLoginOtpVerification] = useState(false);
  const [userEmailForOtp, setUserEmailForOtp] = useState("");
  const [loginResponseData, setLoginResponseData] = useState(null);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath =
    new URLSearchParams(location.search).get("redirect") || "/search";

  const [notification, setNotification] = useState(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  useEffect(() => {
    if (otpTimer > 0) {
      timerRef.current = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResendOtp(true);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [otpTimer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleResendOtp = async () => {
    if (!canResendOtp && activeTab == "signup" && !watchSignup("email")) return;
    if (!canResendOtp && activeTab == "login" && !watchLogin("email")) return;

    try {
      const otpEmail =
        activeTab == "signup" ? watchSignup("email") : watchLogin("email");
      // If no email is available, return early
      if (!otpEmail) {
        toast.error("Email is required to resend OTP");
        return;
      }

      const payload = {
        email: otpEmail,
      };

      const response = await dispatch(resendOtp(payload)).unwrap();

      // Reset timer only if not called from login flow
      if (activeTab == "signup") {
        if (!watchSignup("email")) {
          setOtpTimer(120);
          setCanResendOtp(false);
        }
      } else {
        if (!watchLogin("email")) {
          setOtpTimer(120);
          setCanResendOtp(false);
        }
      }

      // Show success message
      toast.success("OTP resent successfully!");
    } catch (error) {
      toast.error("error", error || "Failed to resend OTP");
    }
  };

  const [showOtpField, setShowOtpField] = useState(false);

  // LOGIN FORM
  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    watch: watchLogin,
    reset: resetLogin,
    formState: { errors: loginErrors, isSubmitting: isLoginSubmitting },
  } = useForm({
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
  });

  // SIGNUP FORM
  const {
    register: registerSignup,
    handleSubmit: handleSubmitSignup,
    watch: watchSignup,
    formState: { errors: signupErrors, isSubmitting: isSignupSubmitting },
  } = useForm({
    defaultValues: { firstName: "", lastName: "", email: "", password: "" },
    mode: "onBlur",
  });

  // LOGIN OTP FORM (for OTP verification after login attempt)
  const {
    register: registerLoginOtp,
    handleSubmit: handleSubmitLoginOtp,
    formState: { errors: loginOtpErrors, isSubmitting: isLoginOtpSubmitting },
  } = useForm({
    defaultValues: { otp: "" },
    mode: "onBlur",
  });

  const { isLoading } = useAuth();
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // LOGIN API
  // LOGIN API

  const showWelcomeThenRedirect = (message, delay = 1500) => {
    setWelcomeMessage(message);
    setShowWelcomeScreen(true);

    setTimeout(() => {
      setShowWelcomeScreen(false);
      onClose(); // close auth modal
      navigate(redirectPath);
    }, delay);
  };

  const onLogin = async (values) => {
    try {
      const response = await dispatch(
        loginUser({ email: values.email, password: values.password }),
      ).unwrap();

      // Check if access_token is null AND otp_verify_status is false
      if (!response.access_token && response.otp_verify_status === false) {
        await handleResendOtp(values.email);
        // User needs OTP verification - no token yet
        setLoginOtpVerification(true);
        setUserEmailForOtp(values.email);
        setLoginResponseData(response);

        // Start OTP timer
        setOtpTimer(120);
        setCanResendOtp(false);

        toast.info("Please verify OTP to complete login");
      } else if (response.access_token && response.otp_verify_status === true) {
        // User is fully verified, log them in
        showWelcomeThenRedirect("Welcome back to Maxipitch!", 3000);

        onClose();
      } else {
        // Handle other cases

        toast.error(response.detail);
      }
    } catch (errMessage) {
      toast.error(errMessage);
    }
  };

  // VERIFY LOGIN OTP API
  const handleVerifyLoginOtp = async (values) => {
    setIsVerifyingOtp(true);
    try {
      const payload = {
        email: userEmailForOtp,
        otp: values.otp,
      };

      const response = await dispatch(verifyOtp(payload)).unwrap();

      if (response.success) {
        toast.success("OTP verified successfully!");

        // Now try to login again
        try {
          const loginResponse = await dispatch(
            loginUser({
              email: userEmailForOtp,
              password: watchLogin("password"), // Get password from form
            }),
          ).unwrap();

          if (loginResponse.access_token) {
            showWelcomeThenRedirect("👋 Welcome back to Maxipitch!", 5000);
            setLoginOtpVerification(false);
            resetLogin();
            onClose();
          }
        } catch (error) {
          setTimeout(() => {
            toast.error("Login failed after OTP verification");
          }, 4000);
        }
      }
    } catch (error) {
      setTimeout(() => {
        toast.error(error || "OTP verification failed");
      }, 4000);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // SIGNUP API (First step - register user)
  const onSignup = async (values) => {
    try {
      const signupResponse = await dispatch(
        signUpUser({
          email: values.email,
          session_id: localStorage.getItem("sessionId")
            ? localStorage.getItem("sessionId")
            : "",
          password: values.password,
        }),
      ).unwrap();

      if (signupResponse.success && !signupResponse.otp_verify_status) {
        // Show OTP field when signup requires OTP verification
        setShowOtpField(true);
        setOtpTimer(120);
        setCanResendOtp(false);
        toast.success("OTP sent to your email!");
      } else {
        toast.error(error || "Signup failed");
      }
    } catch (error) {
      toast.error(error || "Signup failed");
    }
  };

  // VERIFY SIGNUP OTP API (Second step - verify OTP)
  const handleVerifySignupOtp = async (otpValue) => {
    setIsVerifyingOtp(true);
    try {
      const payload = {
        email: watchSignup("email"),
        otp: otpValue,
      };

      const response = await dispatch(verifyOtp(payload)).unwrap();

      if (response.success) {
        toast.success("OTP verified successfully!");

        // Auto login after successful OTP verification
        setIsAutoLoggingIn(true);
        try {
          const loginResponse = await dispatch(
            loginUser({
              email: watchSignup("email"),
              password: watchSignup("password"),
            }),
          ).unwrap();

          if (loginResponse.access_token) {
            toast.success("Signup & Login successful!");
            setShowOtpField(false);
            onClose();
            navigate(redirectPath);
          }
        } catch (error) {
          toast.warning("Signup successful! Please log in manually.");
        } finally {
          setIsAutoLoggingIn(false);
        }
      }
    } catch (error) {
      toast.error(error || "OTP verification failed");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Modified signup form submit handler
  const onSignupSubmit = async (values) => {
    if (showOtpField) {
      // If OTP field is shown, verify OTP instead of signing up
      await handleVerifySignupOtp(values.otp);
    } else {
      // If OTP field is not shown, proceed with normal signup
      await onSignup(values);
    }
  };

  const submitting =
    isLoading ||
    isLoginSubmitting ||
    isSignupSubmitting ||
    isAutoLoggingIn ||
    isVerifyingOtp ||
    isLoginOtpSubmitting;

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false);
  };

  const handleBackToLogin = () => {
    setLoginOtpVerification(false);
    resetLogin();
  };

  return (
    <>
      {/* 🚀 HIDE AUTHMODAL WHEN FORGOT PASSWORD MODAL IS OPEN */}
      {!showForgotPassword && (
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) {
              onClose();
              // Reset states when modal closes
              setLoginOtpVerification(false);
              setShowOtpField(false);
              resetLogin();
            }
          }}
        >
          <DialogContent
            className="sm:max-w-[500px] w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-3rem)]"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="mb-2">
                {loginOtpVerification
                  ? "Verify OTP to Login"
                  : activeTab === "login"
                    ? "Sign In to Maxipitch"
                    : showOtpField
                      ? "Verify OTP"
                      : "Sign Up to Maxipitch"}
              </DialogTitle>
              <DialogDescription>
                {loginOtpVerification
                  ? `Enter the OTP sent to ${userEmailForOtp}`
                  : activeTab === "login"
                    ? "Sign in to your account to continue."
                    : showOtpField
                      ? "Enter the OTP sent to your email"
                      : "Create your account to get started."}
              </DialogDescription>
            </DialogHeader>

            {/* LOGIN OTP VERIFICATION SECTION */}
            {loginOtpVerification ? (
              <form
                onSubmit={handleSubmitLoginOtp(handleVerifyLoginOtp)}
                className="space-y-4 py-4"
                noValidate
              >
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      OTP has been sent to <strong>{userEmailForOtp}</strong>
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <Label htmlFor="login-verify-otp">Verification Code</Label>
                    <div className="text-sm text-gray-600">
                      {otpTimer > 0 ? (
                        <span>Expires in: {formatTime(otpTimer)}</span>
                      ) : (
                        <span className="text-red-600">OTP expired</span>
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <Input
                      id="login-verify-otp"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter 6-digit OTP"
                      {...registerLoginOtp("otp", {
                        required: "OTP is required",
                        minLength: {
                          value: 6,
                          message: "OTP must be 6 digits",
                        },
                        maxLength: {
                          value: 6,
                          message: "OTP must be 6 digits",
                        },
                        pattern: {
                          value: /^[0-9]{6}$/,
                          message: "Enter a valid 6-digit OTP",
                        },
                      })}
                      className="pr-32"
                    />

                    {/* Show/Hide Password Button */}
                    <button
                      type="button"
                      className="absolute  right-24 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>

                    {/* Resend OTP Button */}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={!canResendOtp}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-sm ${
                        canResendOtp
                          ? "text-blue-600 hover:text-blue-800 cursor-pointer"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Resend OTP
                    </button>
                  </div>

                  {loginOtpErrors.otp && (
                    <p className="text-red-500 text-sm mt-1">
                      {loginOtpErrors.otp.message}
                    </p>
                  )}

                  {/* <div className="text-sm text-gray-600">
                    <p>Didn't receive the code? Check your spam folder or</p>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={!canResendOtp}
                      className={`mt-1 font-medium ${
                        canResendOtp
                          ? "text-blue-600 hover:text-blue-800"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      click here to resend
                    </button>
                  </div> */}
                </div>

                <DialogFooter className="flex flex-col space-y-2 justify-center items-center">
                  <Button
                    type="submit"
                    disabled={isVerifyingOtp}
                    className="w-full main-button"
                  >
                    {isVerifyingOtp ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      "Verify & Login"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <Tabs
                defaultValue={activeTab}
                value={activeTab}
                onValueChange={(value) => {
                  setActiveTab(value);
                  setShowOtpField(false); // Reset OTP field when switching tabs
                }}
                className="w-full pt-4"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger
                    value="login"
                    className="data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
                  >
                    Log In
                  </TabsTrigger>
                  <TabsTrigger
                    value="signup"
                    className="data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>

                {/* LOGIN TAB */}
                <TabsContent value="login">
                  <form
                    onSubmit={handleSubmitLogin(onLogin)}
                    className="space-y-4 py-4"
                    noValidate
                  >
                    {/* EMAIL */}
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        {...registerLogin("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Enter a valid email",
                          },
                        })}
                      />
                      {loginErrors.email && (
                        <p className="text-red-500 text-sm">
                          {loginErrors?.email?.message}
                        </p>
                      )}
                    </div>

                    {/* PASSWORD */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">Password</Label>

                        <Button
                          variant="link"
                          className="p-0 font-semibold h-auto text-xs text-blue-600 hover:text-blue-800"
                          type="button"
                          onClick={handleForgotPasswordClick}
                        >
                          Forgot password?
                        </Button>
                      </div>

                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...registerLogin("password", {
                            required: "Password is required",
                          })}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                          onClick={() => setShowLoginPassword((prev) => !prev)}
                        >
                          {showLoginPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>

                      {loginErrors.password && (
                        <p className="text-red-500 text-sm">
                          {loginErrors?.password?.message}
                        </p>
                      )}
                    </div>

                    <DialogFooter>
                      <Button
                        type="submit"
                        disabled={isLoginSubmitting}
                        className="w-full main-button"
                      >
                        {isLoginSubmitting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Log In"
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                {/* SIGNUP TAB */}
                <TabsContent value="signup">
                  <form
                    onSubmit={handleSubmitSignup(onSignupSubmit)}
                    className="space-y-4 py-4"
                    noValidate
                  >
                    {!showOtpField ? (
                      // Regular Signup Form
                      <>
                        {/* EMAIL */}
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email</Label>
                          <Input
                            id="signup-email"
                            type="email"
                            placeholder="you@example.com"
                            {...registerSignup("email", {
                              required: "Email is required",
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Enter a valid email",
                              },
                            })}
                          />
                          {signupErrors.email && (
                            <p className="text-red-500 text-sm">
                              {signupErrors?.email?.message}
                            </p>
                          )}
                        </div>

                        {/* PASSWORD */}
                        {/* PASSWORD */}
                        <div className="space-y-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <div className="relative">
                            <Input
                              id="signup-password"
                              type={showSignupPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              {...registerSignup("password", {
                                required: "Password is required",
                                minLength: {
                                  value: 12,
                                  message:
                                    "Password must be at least 12 characters",
                                },
                              })}
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              onClick={() =>
                                setShowSignupPassword((prev) => !prev)
                              }
                            >
                              {showSignupPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>

                          {/* Password requirement message and counter */}
                          <div className="text-sm space-y-1 px-2">
                            <div className="flex justify-between items-center">
                              <span
                                className={`${watchSignup("password")?.length >= 12 ? "text-green-600 text-xs font-medium" : "text-gray-500 text-xs font-medium"}`}
                              >
                                12 characters required
                              </span>
                              {watchSignup("password") && (
                                <span
                                  className={`font-medium ${
                                    watchSignup("password").length >= 12
                                      ? "text-emerald-500"
                                      : watchSignup("password").length > 0
                                        ? "text-blue-500"
                                        : "text-gray-500"
                                  }`}
                                >
                                  {watchSignup("password").length}/12
                                </span>
                              )}
                            </div>

                            {/* Progress bar */}
                            {watchSignup("password") && (
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all duration-300 ${
                                    watchSignup("password").length >= 12
                                      ? "bg-green-600"
                                      : "bg-blue-500"
                                  }`}
                                  style={{
                                    width: `${Math.min((watchSignup("password").length / 12) * 100, 100)}%`,
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {signupErrors.password && (
                            <p className="text-red-500 text-sm">
                              {signupErrors?.password?.message}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      // OTP Verification Form
                      <>
                        <div className="space-y-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-blue-800">
                              OTP has been sent to{" "}
                              <strong>{watchSignup("email")}</strong>
                            </p>
                            <p className="text-xs text-blue-800 pt-2">Your OTP has been sent to your inbox. If it's not there, please check your spam folder.</p>
                          </div>

                          <div className="flex justify-between items-center">
                            <Label htmlFor="signup-otp">
                              Verification Code
                            </Label>
                            <div className="text-sm text-gray-600">
                              {otpTimer > 0 ? (
                                <span>Expires in: {formatTime(otpTimer)}</span>
                              ) : (
                                <span className="text-red-600">
                                  OTP expired
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="relative">
                            <Input
                              id="signup-otp"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter 6-digit OTP"
                              {...registerSignup("otp", {
                                required: "OTP is required",
                                minLength: {
                                  value: 6,
                                  message: "OTP must be 6 digits",
                                },
                                maxLength: {
                                  value: 6,
                                  message: "OTP must be 6 digits",
                                },
                                pattern: {
                                  value: /^[0-9]{6}$/,
                                  message: "Enter a valid 6-digit OTP",
                                },
                              })}
                              className="pr-32"
                            />

                            {/* Show/Hide Password Button */}
                            <button
                              type="button"
                              className="absolute right-24 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>

                            {/* Resend OTP Button */}
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={!canResendOtp}
                              className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-sm ${
                                canResendOtp
                                  ? "text-blue-600 hover:text-blue-800 cursor-pointer"
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                            >
                              Resend OTP
                            </button>
                          </div>

                          {signupErrors.otp && (
                            <p className="text-red-500 text-sm mt-1">
                              {signupErrors.otp.message}
                            </p>
                          )}

                          {/* <div className="text-sm text-gray-600">
                            <p>
                              Didn't receive the code? Check your spam folder or
                            </p>
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={!canResendOtp}
                              className={`mt-1 font-medium ${
                                canResendOtp
                                  ? "text-blue-600 hover:text-blue-800"
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                            >
                              click here to resend
                            </button>
                          </div> */}
                        </div>
                      </>
                    )}

                    <DialogFooter>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full main-button"
                      >
                        {showOtpField ? (
                          isVerifyingOtp ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Verifying...
                            </>
                          ) : (
                            "Verify OTP"
                          )
                        ) : isSignupSubmitting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Sign Up"
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      )}

      {showWelcomeScreen && (
        <SuccessPopup
          onClose={() => {
            setShowWelcomeScreen(false);
            onClose(); // close auth modal
            navigate(redirectPath);
          }}
        />
      )}

      {/* NOTIFICATION */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* FORGOT PASSWORD MODAL */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={handleForgotPasswordClose}
      />
    </>
  );
}
